import { useEffect, useState, useRef } from 'react'

// Animation timing constants
const DEFAULT_TYPING_SPEED = 80
const DEFAULT_INTERVAL_END_SENTENCE = 7000
const DEFAULT_INTERVAL_WAIT_NEW_SENTENCE = 1000

interface UseTypingAnimationProps {
  sentences: string[]
  typingSpeed?: number
  intervalEndSentence?: number
  intervalWaitNewSentence?: number
}

interface UseTypingAnimationReturn {
  displayText: string
  showCursor: boolean
  currentSentence: string
}

/**
 * Custom hook for creating typing animation effects
 *
 * Provides a realistic typewriter animation that cycles through multiple sentences.
 * Each sentence is typed character by character, then after a pause, it's deleted
 * character by character before moving to the next sentence. Uses requestAnimationFrame
 * for smooth performance and proper cleanup.
 *
 * @param options - Animation configuration
 * @param options.sentences - Array of sentences to cycle through
 * @param options.typingSpeed - Milliseconds between each character (default: 80ms)
 * @param options.intervalEndSentence - Milliseconds to wait at end of sentence (default: 7000ms)
 * @param options.intervalWaitNewSentence - Milliseconds to wait before new sentence (default: 1000ms)
 *
 * @returns Object containing:
 *   - displayText: The current partial sentence being displayed
 *   - showCursor: Whether to show the blinking cursor
 *   - currentSentence: The full sentence currently being animated
 *
 * @example
 * ```tsx
 * const { displayText, showCursor, currentSentence } = useTypingAnimation({
 *   sentences: ['Welcome to our site', 'Discover amazing places', 'Book your adventure'],
 *   typingSpeed: 100,
 *   intervalEndSentence: 3000
 * })
 *
 * return (
 *   <h1>
 *     <span>{displayText}</span>
 *     {showCursor && <span className="cursor">|</span>}
 *   </h1>
 * )
 * ```
 */
export function useTypingAnimation({
  sentences,
  typingSpeed = DEFAULT_TYPING_SPEED,
  intervalEndSentence = DEFAULT_INTERVAL_END_SENTENCE,
  intervalWaitNewSentence = DEFAULT_INTERVAL_WAIT_NEW_SENTENCE,
}: UseTypingAnimationProps): UseTypingAnimationReturn {
  const requestAnimationFrameRef = useRef<number | null>(null)
  const lastTimeWaitingEndRef = useRef<number | undefined>(undefined)
  const lastTimeWaitingNewSentenceRef = useRef<number | undefined>(undefined)
  const lastTimeTypingRef = useRef<number | undefined>(undefined)
  const currentSentenceIndex = useRef<number>(0)
  const currentDirection = useRef<'forward' | 'backward'>('forward')

  const [currentTypingIndex, setCurrentTypingIndex] = useState(0)
  const [showCursor, setShowCursor] = useState(true)

  useEffect(() => {
    const handleSentenceEnd = () => {
      setShowCursor(true)
      currentDirection.current = 'backward'
      lastTimeWaitingEndRef.current = undefined
      lastTimeTypingRef.current = performance.now()
    }

    const handleNewSentence = () => {
      currentSentenceIndex.current = (currentSentenceIndex.current + 1) % sentences.length
      currentDirection.current = 'forward'
      setShowCursor(true)
      lastTimeWaitingNewSentenceRef.current = undefined
      lastTimeTypingRef.current = performance.now()
    }

    const handleTypingForward = (prev: number, timestamp: number) => {
      if (prev + 1 <= sentences[currentSentenceIndex.current].length) {
        lastTimeTypingRef.current = timestamp
        return prev + 1
      }

      lastTimeTypingRef.current = undefined
      lastTimeWaitingEndRef.current = performance.now()
      setShowCursor(false)
      return prev
    }

    const handleTypingBackward = (prev: number, timestamp: number) => {
      if (prev - 1 >= 0) {
        lastTimeTypingRef.current = timestamp
        return prev - 1
      }

      lastTimeTypingRef.current = undefined
      lastTimeWaitingNewSentenceRef.current = performance.now()
      return prev
    }

    const handleTyping = (timestamp: number) => {
      setCurrentTypingIndex(prev => {
        if (currentDirection.current === 'forward') {
          return handleTypingForward(prev, timestamp)
        }
        return handleTypingBackward(prev, timestamp)
      })
    }

    // Main animation loop
    const tick = (timestamp: number) => {
      // Check if we need to end the current sentence
      if (
        lastTimeWaitingEndRef.current &&
        timestamp - lastTimeWaitingEndRef.current >= intervalEndSentence
      ) {
        handleSentenceEnd()
      }

      // Check if we need to start a new sentence
      if (
        lastTimeWaitingNewSentenceRef.current &&
        timestamp - lastTimeWaitingNewSentenceRef.current >= intervalWaitNewSentence
      ) {
        handleNewSentence()
      }

      // Check if we need to type the next character
      if (lastTimeTypingRef.current && timestamp - lastTimeTypingRef.current >= typingSpeed) {
        handleTyping(timestamp)
      }

      requestAnimationFrameRef.current = requestAnimationFrame(tick)
    }

    // Only start animation if we have sentences
    if (sentences.length > 0) {
      lastTimeTypingRef.current = performance.now()
      requestAnimationFrameRef.current = requestAnimationFrame(tick)
    }

    return () => {
      if (requestAnimationFrameRef.current) {
        cancelAnimationFrame(requestAnimationFrameRef.current)
        requestAnimationFrameRef.current = null
      }
    }
  }, [sentences, typingSpeed, intervalEndSentence, intervalWaitNewSentence])

  const currentSentence = sentences[currentSentenceIndex.current] || ''
  const displayText = currentSentence.slice(0, currentTypingIndex)

  return {
    displayText,
    showCursor,
    currentSentence,
  }
}
